import { ReactElement } from 'react';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { logger } from '../../../../../util/logger';
import { ExecutionInfo } from '../../hook/live-execution.hooks';
import {
  FlowItemArrayType,
  FlowValidateError,
  SystemUnit,
} from '../../model/flow.types';
import {
  getFlowItemPlaceholderByType,
  instanceOfHelper,
  instanceOfPlaceholder,
  instanceOfSectionEnd,
} from '../../util/flow-item.util';
import { FlowItemAction } from '../flow-item/view/flow-item-base';
import { FlowItemBoundary } from '../flow-item/view/flow-item-boundary';
import { useFlowRefContext } from './flow-ref';

interface FlowItemRowProps {
  flowItem: FlowItemArrayType;
  flowValidationErrors?: FlowValidateError[] | undefined;
  index: number;
  selectedItems: string[];
  onAddItemsAfter: (index?: number) => Promise<void>;
  canAddReorderItem: boolean;
  onSelectItem?: (id: string) => void;
  getActions: (index: number) => FlowItemAction[] | undefined;
  executionInfo?: ExecutionInfo;
  isStartItem?: boolean;
  isInactive?: boolean;
  onLongPress?: (e: Event, id: string) => void;
  presentItemQuickActions?: (e: Event, id: string) => void;
  label?: string;
  classes?: string[];
  addPrecedingStem?: boolean;
  isSectionSelected?: boolean;
  system: SystemUnit;
}

const FlowItemRow = (props: FlowItemRowProps): ReactElement => {
  const {
    canAddReorderItem,
    index,
    flowItem,
    flowValidationErrors,
    getActions,
    onAddItemsAfter,
    onSelectItem,
    selectedItems,
    executionInfo,
    isStartItem,
    isInactive,
    onLongPress,
    label,
    presentItemQuickActions,
    classes,
    addPrecedingStem,
    isSectionSelected,
    system,
  } = props;

  const {
    componentRegistry: { FlowItem: FlowItemComponent },
  } = useFlowRefContext();

  if (instanceOfHelper(flowItem)) {
    if (instanceOfPlaceholder(flowItem)) {
      const classNames = ['placeholder-item', ...(classes ?? [])];
      return (
        <div className={classNames.join(' ')}>
          {getFlowItemPlaceholderByType(flowItem.type)}
        </div>
      );
    } else if (instanceOfSectionEnd(flowItem)) {
      return canAddReorderItem ? (
        <FlowItemBoundary text={getLocalizedText('common:section_end')} />
      ) : (
        <div></div>
      );
    }

    logger.error('Unsupported helper type', {
      flowItem,
    });
    return <></>;
  }

  let hasError = false;

  flowValidationErrors?.forEach((e) => {
    if (index === e.startIndex) {
      hasError = true;
    } else if (index > e.startIndex && index < e.endIndex) {
      hasError = true;
    }
  });

  const classNames = [`flow-item`, `item-${flowItem.id}`, ...(classes ?? [])];

  if (!flowItem.id) {
    classNames.push('new-item');
  }

  return (
    <div className={classNames.join(' ')}>
      <FlowItemComponent
        flowItem={flowItem}
        onAddItemsBefore={() => onAddItemsAfter(Math.max(index - 1, 0))}
        onAddItemsAfter={() => onAddItemsAfter(index)}
        isItemSelected={
          flowItem?.id !== undefined
            ? selectedItems.includes(flowItem?.id)
            : false
        }
        isSectionSelected={isSectionSelected}
        highlight={!flowItem.id || hasError}
        canAddItem={canAddReorderItem}
        canReorderItem={canAddReorderItem}
        onSelectItem={
          onSelectItem
            ? () => {
                if (flowItem.id) {
                  onSelectItem(flowItem.id);
                }
              }
            : undefined
        }
        actions={getActions(index)}
        system={system}
        executionInfo={executionInfo}
        isStartItem={isStartItem}
        isInactive={isInactive}
        onLongPress={onLongPress}
        label={label}
        presentItemQuickActions={presentItemQuickActions}
        addPrecedingStem={addPrecedingStem}
      />
    </div>
  );
};

export { FlowItemRow };
